import { DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, Injector, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxIconName, OnyxOption } from '@onyx/angular';
import {
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxIconComponent,
  OnyxModalComponent,
  OnyxRadioButtonComponent,
} from '@onyx/angular';
import { forkJoin, Subject } from 'rxjs';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../../common/services/dictionaries.service';
import { ValidationService } from '../../../../../common/services/validation.service';
import { FleetCategory } from '../../enums/fleet-category';
import { FleetRoute } from '../../enums/fleet-route';
import { FleetRouterStateKey } from '../../enums/fleet-router-state-key';
import { NavigationHelper } from '../../../../../common/helpers/navigation.helper';

enum FormStep {
  CATEGORY,
  TYPE,
}

@Component({
  selector: 'app-add-fleet-modal',
  imports: [
    OnyxModalComponent,
    TranslatePipe,
    ReactiveFormsModule,
    AsyncPipe,
    OnyxIconComponent,
    OnyxRadioButtonComponent,
    OnyxButtonComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './add-fleet-modal.component.html',
  styleUrl: './add-fleet-modal.component.scss',
})
export class AddFleetModalComponent {
  protected readonly dialogRef = inject(DialogRef);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);
  private readonly validationService = inject(ValidationService);
  private readonly router = inject(Router);

  protected readonly I18N = 'fleet.addFleetModal';
  protected readonly FormStep = FormStep;

  protected readonly categories$ =
    this.dictionariesService.getFleetCategories();

  protected types = signal<OnyxOption<string>[]>([]);

  protected form = this.buildForm();
  protected formStep = computed(() =>
    this.step() === FormStep.CATEGORY || this.types().length === 0
      ? FormStep.CATEGORY
      : FormStep.TYPE,
  );
  protected loading = signal(false);
  protected close$ = new Subject<void>();

  protected categoryControl = this.form.controls.category;
  protected category = toSignal(this.categoryControl.valueChanges, {
    initialValue: this.categoryControl.value,
  });

  protected typeControl = this.form.controls.type;
  protected type = toSignal(this.typeControl.valueChanges, {
    initialValue: this.typeControl.value,
  });

  protected disabled = computed(
    () =>
      this.loading() ||
      (this.step() === FormStep.CATEGORY ? !this.category() : !this.type()),
  );

  private step = signal(FormStep.CATEGORY);

  protected async continue(): Promise<void> {
    const control = {
      [FormStep.CATEGORY]: this.categoryControl,
      [FormStep.TYPE]: this.typeControl,
    }[this.step()];
    if (!(await ValidationHelper.checkValidity(control, this.injector))) return;

    if (this.step() === FormStep.CATEGORY) {
      this.form.disable();
      this.loading.set(true);

      const category = this.category()!;
      forkJoin([
        this.dictionariesService.getDictionary(DictionaryCode.VEHICLE_TYPE),
        this.validationService.getFleetValidation(category),
      ]).subscribe(([vehicleTypes, validation]) => {
        const allowedTypes = Object.keys(validation[category]);
        this.types.set(
          vehicleTypes.filter((type) => allowedTypes.includes(type.value)),
        );
        this.step.set(FormStep.TYPE);

        if (this.types().length === 0) {
          ValidationHelper.toggleControls(this.typeControl, {
            enabled: false,
            reset: true,
          });
          this.redirectToForm();
        } else {
          this.form.enable();
          this.loading.set(false);
        }
      });
    } else {
      this.redirectToForm();
    }
  }

  protected back(): void {
    this.formStep() === FormStep.CATEGORY
      ? this.close$.next()
      : this.step.set(FormStep.CATEGORY);
  }

  protected getFleetIcon(category: FleetCategory): OnyxIconName {
    return `${category}-filled`;
  }

  private redirectToForm(): void {
    const { category, type } = this.form.getRawValue();
    this.router.navigateByUrl(
      FleetRoute.ADD_FLEET,
      NavigationHelper.withOrigin(this.router, {
        state: {
          [FleetRouterStateKey.CATEGORY]: category,
          [FleetRouterStateKey.TYPE]: type,
        },
      }),
    );

    this.close$.next();
  }

  private buildForm() {
    return this.fb.group({
      category: this.fb.control<FleetCategory>(FleetCategory.SEMI_TRUCK, [
        Validators.required,
      ]),
      type: this.fb.control<string | null>(null, [Validators.required]),
    });
  }
}
